// i18n context (PL/EN). All UI text routes through `t()` — GITEX RULE #2
// forbids hardcoded strings in components.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { en, type MessageKey } from '../i18n/en';
import { pl } from '../i18n/pl';

export type Language = 'en' | 'pl';

const catalogs: Record<Language, Record<MessageKey, string>> = { en, pl };

interface LanguageContextValue {
  readonly lang: Language;
  readonly setLang: (lang: Language) => void;
  readonly t: (key: MessageKey) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }): JSX.Element {
  const [lang, setLang] = useState<Language>('en');
  const t = useCallback((key: MessageKey): string => catalogs[lang][key], [lang]);
  const value = useMemo<LanguageContextValue>(() => ({ lang, setLang, t }), [lang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (ctx === null) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
}
